import { Children, cloneElement, isValidElement, ReactNode } from 'react';

interface DocsStepProps {
  title: string;
  children?: ReactNode;
  /** Injected by DocsSteps — do not pass manually. */
  index?: number;
  /** Injected by DocsSteps — hides the connector rail on the final step. */
  isLast?: boolean;
}

/**
 * One numbered step. Only meaningful inside <DocsSteps>.
 */
export const DocsStep: React.FC<DocsStepProps> = ({ title, children, index = 1, isLast }) => (
  <li className="relative pb-8 pl-12 last:pb-0">
    {!isLast && (
      <span className="absolute bottom-0 left-[15px] top-9 w-px bg-slate-200" aria-hidden="true" />
    )}
    <span className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full border border-primary-100 bg-primary-50 text-sm font-semibold text-primary-700">
      {index}
    </span>
    <h4 className="!mb-2 !mt-0.5 text-base font-semibold text-slate-900">{title}</h4>
    {children && (
      <div className="text-[15px] leading-relaxed text-slate-600 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        {children}
      </div>
    )}
  </li>
);

interface DocsStepsProps {
  children: ReactNode;
}

/**
 * Vertical numbered walkthrough with a connecting rail.
 *
 * Articles previously used `<ol className="space-y-4">` with a bold `<strong>`
 * per item, which read as a wall of paragraphs. This gives procedures a real
 * visual spine and makes "where am I" obvious at a glance.
 */
const DocsSteps: React.FC<DocsStepsProps> = ({ children }) => {
  const steps = Children.toArray(children).filter(isValidElement);

  return (
    <ol className="not-prose my-8 list-none !p-0">
      {steps.map((step, i) =>
        isValidElement<DocsStepProps>(step)
          ? cloneElement(step, { index: i + 1, isLast: i === steps.length - 1 })
          : step
      )}
    </ol>
  );
};

export default DocsSteps;
